import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

/** Lists longer than this fold the rest behind a "show more" toggle. */
export const COLLAPSE_LIMIT = 3;

/**
 * Renders the first few items of a list and folds the rest behind a toggle.
 * Returns a fragment, so the host keeps its own layout (chip row, stacked list):
 * the visible items and the toggle become siblings inside the host's container.
 */
export default function CollapsibleItems<T>({
  items,
  limit = COLLAPSE_LIMIT,
  toggleClassName,
  children,
}: {
  items: readonly T[];
  limit?: number;
  toggleClassName?: string;
  children: (visible: readonly T[]) => ReactNode;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const collapsible = items.length > limit;
  const visible = collapsible && !expanded ? items.slice(0, limit) : items;

  return (
    <>
      {children(visible)}
      {collapsible && (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded(!expanded)}
          className={cn(
            "self-center rounded-md px-1.5 py-0.5 text-xs font-semibold text-secondary hover:bg-secondary/10",
            toggleClassName,
          )}
        >
          {expanded
            ? t("tickets.attachmentsToggle.less")
            : t("tickets.attachmentsToggle.more", {
                count: items.length - limit,
              })}
        </button>
      )}
    </>
  );
}
